import {
  useQuery,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { DateTime } from 'luxon';

const apiURL = import.meta.env.VITE_GCX_SERVICE_URI;

/** @typedef {object} GCXScheduleItem
 * @property {string} day
 * @property {number} endAmount
 * @property {string} endTimestamp
 * @property {number} startAmount
 * @property {string} startTimestamp
 * @property {string} streamerName
 * @property {string} timeBlockLabel
 * @property {string} id
 * @property {string} year
 */

/** @typedef {object} GCXScheduleData
 * @property {GCXScheduleItem[]} items
 * @property {string[]} days
 * @property {string[]} timeBlockLabels
 */

/**
 * Get GCX totals for the current year.
 * @returns {{items: GCXScheduleItem[], timeBlockLabels: string[], isLoadingTotals: boolean, lastUpdated: DateTime|null, refreshData: function, days: string[], currentSchedule: GCXScheduleItem|null}}
 */
export default function useGCXTotals() {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const { data: responseFromQuery, isLoading } = useQuery({
    queryKey: ['gcx-totals'],
    initialData: [],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const response = await fetch(`${apiURL}all`);
      const now = DateTime.fromJSDate(new Date());
      setLastUpdated(now);
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      /**
       * @type {GCXScheduleData}
       */
      return (await response.json()) ?? null;
    },
    refetchInterval: 60000, // Refetch every minute
  });

  const { data: currentSchedule, isLoading: isLoadingCurrentSchedule } =
    useQuery({
      queryKey: ['gcx-current-schedule'],
      initialData: null,
      placeholderData: keepPreviousData,
      queryFn: async () => {
        const response = await fetch(`${apiURL}schedule`);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        /**
         * @type {GCXScheduleItem[]}
         */
        const items = await response.json();
        return items[0] ?? null;
      },
      refetchInterval: 60000, // Refetch every minute
    });

  useEffect(() => {
    setLoading(isLoading && isLoadingCurrentSchedule);
  }, [isLoading, isLoadingCurrentSchedule]);

  const refreshData = useCallback(() => {
    setLoading(true);
    queryClient.invalidateQueries({ queryKey: ['gcx-totals'] }).then(() => {
      setLoading(false);
    });
  }, [queryClient]);

  /**
   * @type {string[]}
   */
  const timeBlockLabels = useMemo(() => {
    if (responseFromQuery !== undefined && responseFromQuery !== null) {
      return responseFromQuery.timeBlockLabels;
    }

    return [];
  }, [responseFromQuery]);

  /**
   * @type {string[]}
   */
  const days = useMemo(() => {
    if (
      responseFromQuery !== undefined &&
      responseFromQuery !== null &&
      'days' in responseFromQuery
    ) {
      return responseFromQuery.days.sort((a, b) => {
        return a.replaceAll(/\D/g, '').localeCompare(b.replaceAll(/\D/g, ''));
      });
    }

    return [];
  }, [responseFromQuery]);

  /**
   * @typedef {object} ResponseGCXTotals
   * @property {GCXScheduleItem[]} items
   * @property {string[]} timeBlockLabels
   * @property {string[]} days
   * @property {boolean} isLoadingTotals
   * @property {function} refreshData
   * @property {DateTime|null} lastUpdated
   * @property {GCXScheduleItem|null} currentSchedule
   */
  return {
    items: responseFromQuery.items ?? [],
    days: days ?? [],
    timeBlockLabels: timeBlockLabels ?? [],
    isLoadingTotals: loading,
    refreshData,
    lastUpdated,
    currentSchedule,
  };
}
